import React, { useEffect, useMemo, useState } from "react";
import api from "../api";

const MyOrders = () => {
  useEffect(() => {
    document.title = "My Orders | Workgear365";
  }, []);
  const [orders, setOrders] = useState([]);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("success"); // success | danger | warning
  const isMobile = window.innerWidth < 768;
  // Simple helper to trigger toast
  const triggerToast = (message, type = "success") => {
    setToastMsg(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
 

  // Return modal state
  const [showModal, setShowModal] = useState(false);
  const [maxQty, setMaxQty] = useState(1);
  const [returnData, setReturnData] = useState({
    orderItemId: "",
    quantity: 1,
    reason: "",
    comments: "",
    desired_action: "",
    photos: [],
    exchange: { sku: "", name: "", size: "" },
  });

  useEffect(() => {
    api
      .get("/api/my-orders")
      .then((res) => setOrders(res.data?.orders || []))
      .catch((err) => {
        if (err?.response?.status === 401) {
          window.location.href = "/login?next=/my-orders";
        } else {
          console.error("Error fetching orders:", err);
          triggerToast("Failed to load orders", "danger");
        }
      });
  }, []);

  // Toast via ?success=... (optional)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    if (success) {
      triggerToast(success, "success");
    }
  }, []);

  const formatMoney = (v) => {
    const num = Number(v || 0);
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Safe JSON parse helper
  const safeParseJSON = (v) => {
    if (!v) return null;
    if (typeof v === "object") return v;
    try { return JSON.parse(v); } catch { return null; }
  };

  const grouped = useMemo(() => {
    if (!orders || orders.length === 0) return {};

    if (Array.isArray(orders) && orders[0]?.items) {
      const groups = {};
      orders.forEach((ord) => {
        const key = ord.order_id || ord.orderDetailID || ord.id;
        const addr = safeParseJSON(ord.address);

        const normalizedItems = (ord.items || []).map((it) => ({
          ...it,
          Title: it.product_name || it.title,
          salePrice: Number(it.price ?? it.salePrice ?? 0),
          total_quantity: Number(it.quantity ?? 1),
          colorFrontImage: it.colorFrontImage || it.image || it.frontimage || null,
          color: it.color,
          size: it.size,
          return_request_id: it.return_request_id,
          _meta_header: {
            status: (ord.status === "paid" || ord.status === 1 || ord.status === "1") ? "1" : "0",
            created_at: ord.created_at,
            first_name: addr?.first_name || "",
            last_name:  addr?.last_name  || "",
            email:      addr?.email      || "",
            mobile:     addr?.mobile     || "",
            address:    addr?.address    || "",
            city:       addr?.city       || "",
            state:      addr?.state      || "",
            zip:        addr?.zip || addr?.pincode || addr?.zip_code || "",
          },
        }));

        groups[key] = normalizedItems;
      });
      return groups;
    }

    // Fallback: flat rows → group by order_id
    const by = {};
    orders.forEach((row) => {
      const key = row.order_id || row.orderDetailID || row.id;
      if (!by[key]) by[key] = [];
      const addr = safeParseJSON(row.address);

      by[key].push({
        ...row,
        Title: row.Title || row.product_name,
        salePrice: Number(row.salePrice ?? row.price ?? 0),
        total_quantity: Number(row.total_quantity ?? row.quantity ?? 1),
        colorFrontImage: row.colorFrontImage || row.image || row.frontimage || null,
        _meta_header: {
          status: (row.status === "paid" || row.status === 1 || row.status === "1") ? "1" : "0",
          created_at: row.created_at,
          first_name: row.first_name || addr?.first_name || "",
          last_name:  row.last_name  || addr?.last_name  || "",
          email:      row.email      || addr?.email      || "",
          mobile:     row.mobile     || addr?.mobile     || "",
          address:    row.address_text || addr?.address || row.address || "",
          city:       row.city || addr?.city || "",
          state:      row.state || addr?.state || "",
          zip:        row.zip || addr?.zip || addr?.pincode || "",
        },
      });
    });
    return by;
  }, [orders]);

  const orderKeys = Object.keys(grouped);

  // Return modal handlers
  const handleReturnClick = (item) => {
    setReturnData({
      orderItemId: item.id,
      quantity: 1,
      reason: "",
      comments: "",
      desired_action: "",
      photos: [],
      exchange: { sku: "", name: "", size: "" },
    });
    setMaxQty(Number(item.total_quantity || 1));
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReturnData((s) => ({ ...s, [name]: value }));
  };
  const handleFileChange = (e) => {
    setReturnData((s) => ({ ...s, photos: e.target.files }));
  };
  const handleExchangeChange = (e) => {
    const { name, value } = e.target;
    setReturnData((s) => ({ ...s, exchange: { ...s.exchange, [name]: value } }));
  };
  const closeModal = () => setShowModal(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("order_detail_id", returnData.orderItemId);
    fd.append("quantity", returnData.quantity);
    fd.append("reason", returnData.reason);
    fd.append("desired_action", returnData.desired_action);
    if (returnData.reason === "damaged") {
      fd.append("comments", returnData.comments || "");
      Array.from(returnData.photos || []).forEach((f) => fd.append("photos[]", f));
    }
    if (returnData.desired_action === "exchange") {
      fd.append("exchange_sku", returnData.exchange.sku);
      fd.append("exchange_name", returnData.exchange.name);
      fd.append("exchange_size", returnData.exchange.size);
    }

    try {
      await api.get("/sanctum/csrf-cookie");
      await api.post(`/api/orders/${returnData.orderItemId}/return`, fd);
      setOrders((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((ord) => ({
        ...ord,
        items: Array.isArray(ord.items)
          ? ord.items.map((it) =>
              it.id === returnData.orderItemId
                ? { ...it, return_request_id: it.return_request_id ?? 1 }
                : it
            )
          : ord.items,
      }));
    });
      triggerToast("Return request submitted successfully!", "success");
      setShowModal(false);
    } catch (err) {
      if (err?.response?.status === 401) {
        window.location.href = "/login?next=/my-orders";
        return;
      }
      console.error("Return request failed:", err);
      triggerToast("Failed to submit return request.", "danger");
    }
  };

  return (
    <div className="container py-5">
      {/* Toast (top-right) */}
      {showToast && (
        <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
          <div className={`toast show align-items-center text-bg-${toastType} border-0`} role="alert">
            <div className="d-flex">
              <div className="toast-body">{toastMsg}</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                onClick={() => setShowToast(false)}
              />
            </div>
          </div>
        </div>
      )}

      <h3 className="mb-4">My All Orders</h3>

      {orderKeys.length === 0 && <div className="alert alert-info">No Orders Found!</div>}

      {orderKeys.map((orderId) => {
        const group = grouped[orderId] || [];
        const head = group[0]?._meta_header || {};
        const totalPaid = group.reduce(
          (sum, it) => sum + Number(it.salePrice || 0) * Number(it.total_quantity || 0),
          0
        );

        return (
          <div key={orderId} className="card shadow-lg border-0 mb-4">
            <div className="card-header bg-light d-md-flex justify-content-between">
              <h5 className="mb-2 mb-md-0">Order ID: {orderId}</h5>
              <span>
                {head.status === "1" ? (
                  <span className="badge bg-success">Success</span>
                ) : (
                  <span className="badge bg-danger">Cancelled</span>
                )}
              </span>
            </div>

            <div className="card-body">
              <div className="row mb-3 gy-3">
                <div className="col-md-6">
                  <h5>Customer Information</h5>
                  <p><strong>Name:</strong> {head.first_name} {head.last_name}</p>
                  <p><strong>Email:</strong> {head.email}</p>
                  <p><strong>Mobile:</strong> {head.mobile}</p>
                  <p>
                    <strong>Address:</strong> {head.address}
                    {head.address ? ", " : ""}{head.city}
                    {head.city ? ", " : ""}{head.state}
                    {head.zip ? ` - ${head.zip}` : ""}
                  </p>
                </div>
                <div className="col-md-6 text-end">
                  <h5>Order Info</h5>
                  <p><strong>Order ID:</strong> {orderId}</p>
                  <p><strong>Date:</strong> {head.created_at ? new Date(head.created_at).toLocaleString() : ""}</p>
                  <p>
                    <strong>Status: </strong>
                    {head.status === "1" ? (
                      <span className="badge bg-success">Success</span>
                    ) : (
                      <span className="badge bg-danger">Cancelled</span>
                    )}
                  </p>
                </div>
              </div>

              <hr />

              <h5>Product Details</h5>

{isMobile ? (
  // ✅ MOBILE CARD UI
  <div className="row">
    {group.map((it) => (
      <div key={it.id} className="col-12 mb-3">
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="d-flex">
              <img
                src={it.colorFrontImage}
                alt={it.Title}
                style={{ width: 90, height: 90, objectFit: "cover" }}
                className="me-3 rounded"
              />

              <div className="flex-grow-1">
                <h6 className="mb-1">{it.Title}</h6>
                <div className="small text-muted">
                  Color: {it.color || "—"} | Size: {it.size || "—"}
                </div>
                <div className="mt-2">
                  <strong>${formatMoney(it.salePrice)}</strong> × {it.total_quantity}
                </div>
                <div className="mt-1">
                  Total: $
                  {formatMoney(
                    (it.salePrice || 0) * (it.total_quantity || 0)
                  )}
                </div>

                <div className="mt-2">
                  {it.return_request_id ? (
                    <span className="badge bg-success">
                      Return Request Submitted
                    </span>
                  ) : (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleReturnClick(it)}
                    >
                      Request Return
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
) : (
  // ✅ DESKTOP TABLE (same as your old one)
  <div className="table-responsive">
    <table className="table table-bordered table-striped">
      <thead className="table-dark">
        <tr>
          <th>Image</th>
          <th>Product Name</th>
          <th>Color</th>
          <th>Size</th>
          <th>Price</th>
          <th>Qty</th>
          <th>Total</th>
          <th>Request Return</th>
        </tr>
      </thead>
      <tbody>
        {group.map((it) => (
          <tr key={it.id}>
            <td>
              <img src={it.colorFrontImage} width="80" alt="" />
            </td>
            <td>{it.Title}</td>
            <td>{it.color || "—"}</td>
            <td>{it.size || "—"}</td>
            <td>${formatMoney(it.salePrice)}</td>
            <td>{it.total_quantity}</td>
            <td>
              $
              {formatMoney(
                (it.salePrice || 0) * (it.total_quantity || 0)
              )}
            </td>
            <td>
              {it.return_request_id ? (
                <span className="badge bg-success">
                  Return Request Submitted
                </span>
              ) : (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleReturnClick(it)}
                >
                  Request Return
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}

              <div className="text-end">
                <h4> Total Paid: ${formatMoney(totalPaid)} </h4>
              </div>
            </div>
          </div>
        );
      })}

      {/* Return Modal */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.5)" }} aria-modal="true">
          <div className="modal-dialog" role="document">
            <form className="modal-content" onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="modal-header">
                <h5 className="modal-title">Request Return</h5>
                <button type="button" className="btn-close" onClick={closeModal} />
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Quantity to Return</label>
                  <select className="form-control" name="quantity" value={returnData.quantity} onChange={handleChange} required>
                    {Array.from({ length: maxQty }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Reason for Return</label>
                  <select className="form-select" name="reason" value={returnData.reason} onChange={handleChange} required>
                    <option value="">-- Select reason --</option>
                    <option value="wrong_item">Wrong Item</option>
                    <option value="improper_fit">Improper Fit</option>
                    <option value="damaged">Damaged (Describe & attach photos)</option>
                    <option value="not_as_expected">Not what I thought it would be</option>
                  </select>
                </div>

                {returnData.reason === "damaged" && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Comments</label>
                      <textarea className="form-control" name="comments" rows={3} value={returnData.comments} onChange={handleChange} required placeholder="Describe the damage..." />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Attach Photos (up to 5)</label>
                      <input className="form-control" type="file" accept="image/*" multiple onChange={handleFileChange} required />
                      <div className="form-text">Accepted: JPG/PNG/GIF/WebP • Max 5MB each</div>
                    </div>
                  </>
                )}

                <div className="mb-3">
                  <label className="form-label">Desired Action</label>
                  <select className="form-control" name="desired_action" value={returnData.desired_action} onChange={handleChange} required>
                    <option value="">-- Select Action --</option>
                    <option value="refund">Refund</option>
                    <option value="exchange">Exchange</option>
                  </select>
                </div>

                {returnData.desired_action === "exchange" && (
                  <div id="exchange_fields">
                    <div className="mb-2">
                      <label className="form-label">New SKU</label>
                      <input className="form-control" name="sku" value={returnData.exchange.sku} onChange={handleExchangeChange} required />
                    </div>
                    <div className="mb-2">
                      <label className="form-label">Product Name</label>
                      <input className="form-control" name="name" value={returnData.exchange.name} onChange={handleExchangeChange} required />
                    </div>
                    <div className="mb-2">
                      <label className="form-label">Size</label>
                      <input className="form-control" name="size" value={returnData.exchange.size} onChange={handleExchangeChange} required />
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-danger">Submit Request</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;
